// ante-react-kit/src/components/AntLockScreen/index.js
// Khoá màn hình khi idle / khi user gọi lockNow(); mở khoá bằng mã PIN của nhân viên.
import React, { Fragment, useEffect, useState } from 'react';
import AntPasscodeOverlay from '../AntPasscodeOverlay';
import AntChangePasscodePopup from '../AntChangePasscodePopup';
import AntShiftActionOverlay from '../AntShiftActionOverlay';
import useIdleTimer from '../../hooks/useIdleTimer';
import AntAuth from '../../utils/AntAuth';
import cs from '../../utils/const.js';
import './index.scss';
import '../AntChangePasscodePopup/index.scss';

const AUTO_LOCK_MINUTES = 5;
const LOCK_BRUTEFORCE_DELAY_SECONDS = 30;
const LOCK_MAX_FAIL_BEFORE_DELAY = 5;

// PHẢI dùng cs.System_Code (REACT_APP_SYSTEM_CODE = 'ante-erp') khớp với AntAuth.saveSessionData,
// AntVoucher, AntPOSScreen... — trước đây fallback 'ANTE' là sai key, isPOSMode/auto-lock/session-locked
// đều đọc nhầm bucket → ghi chú "Mã mặc định 111111" không reset được sau đổi PIN.
const lsKey = (k) => `${cs.System_Code}-${k}`;

export function isAutoLockEnabled() {
    return localStorage.getItem(lsKey('auto-lock')) !== '0';
}
export function setAutoLockEnabled(on) {
    localStorage.setItem(lsKey('auto-lock'), on ? '1' : '0');
    window.dispatchEvent(new Event('apt-auto-lock-changed'));
}

// Persist lock state — nếu không lưu, user reload là vượt qua lock screen.
function isSessionLocked() {
    return localStorage.getItem(lsKey('session-locked')) === '1';
}
function setSessionLocked(on) {
    if (on) localStorage.setItem(lsKey('session-locked'), '1');
    else localStorage.removeItem(lsKey('session-locked'));
}

// Giữ lockNow() để tương thích — wrapper của AntAuth.lockSession().
export function lockNow() {
    AntAuth.lockSession();
}

// Gating cho cluster vào-ra-đổi ca / sổ quỹ — yêu cầu manages_work_shift = 1 (BE đã AND
// sẵn use_Module_AR + use_Module_POS trong _buildSession khi build token).
export function isShiftFeatureEnabled() {
    if (!AntAuth.isValidSession || !AntAuth.isValidSession()) return false;
    return Number(AntAuth.getSessionInfo && AntAuth.getSessionInfo('manages_work_shift')) === 1;
}

// Gating cho lock screen / auto-lock: chỉ cần có session hợp lệ.
// PIN cá nhân luôn có (mặc định '1111' nếu user chưa cài) → lock luôn dùng được,
// không phụ thuộc cờ manages_work_shift hay module AR/POS.
export function isLockFeatureEnabled() {
    if (!AntAuth.isValidSession || !AntAuth.isValidSession()) return false;
    return true;
}

export default function AntLockScreen() {
    // Init từ localStorage — reload page vẫn giữ lock state, không cho bypass bằng F5.
    // Lock chỉ cần session hợp lệ; không phụ thuộc manages_work_shift.
    const [locked, setLockedState] = useState(() => isLockFeatureEnabled() && isSessionLocked() && AntAuth.isValidSession());
    const setLocked = (on) => {
        if (on && !isLockFeatureEnabled()) return;
        setSessionLocked(on);
        setLockedState(on);
    };
    const [errorText, setErrorText] = useState('');
    const [processing, setProcessing] = useState(false);
    const [failCount, setFailCount] = useState(0);
    const [delayUntil, setDelayUntil] = useState(0);
    const [autoLockOn, setAutoLockOnState] = useState(isAutoLockEnabled());
    const [now, setNow] = useState(Date.now());
    const [showSwitchShift, setShowSwitchShift] = useState(false);
    const [showChangePin, setShowChangePin] = useState(false);
    const [changedNotice, setChangedNotice] = useState('');
    // Bump khi session-info trong localStorage được cập nhật (sau đổi PIN / refresh token) để
    // re-render và đọc flag mới. AntAuth.getSessionInfo đọc localStorage, không phải state.
    const [, setSessionVer] = useState(0);

    const isPOSMode = () => {
        try {
            const raw = localStorage.getItem(lsKey('ui-states'));
            const obj = raw ? JSON.parse(raw) : {};
            return obj.mode === 'POSUI';
        } catch (e) {
            return false;
        }
    };

    useEffect(() => {
        const onChange = () => setAutoLockOnState(isAutoLockEnabled());
        const onLock = () => setLocked(true);
        window.addEventListener('apt-auto-lock-changed', onChange);
        window.addEventListener('apt-lock-now', onLock);
        return () => {
            window.removeEventListener('apt-auto-lock-changed', onChange);
            window.removeEventListener('apt-lock-now', onLock);
        };
    }, []);

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    // AntAuth chỉ có isValidSession() — coi như đại diện cho isAuthenticated()
    const idleEnabled = autoLockOn && isPOSMode() && AntAuth.isValidSession() && !locked && isLockFeatureEnabled();
    useIdleTimer({
        timeoutMs: AUTO_LOCK_MINUTES * 60 * 1000,
        enabled: idleEnabled,
        onIdle: () => setLocked(true)
    });

    const handleComplete = (passCode) => {
        if (delayUntil > Date.now()) return;
        setProcessing(true);
        AntAuth.unlockSession(passCode, (res) => {
            setProcessing(false);
            if (!res || res.error || !res.data || !res.data.unlocked) {
                const next = failCount + 1;
                setFailCount(next);
                setErrorText('Mã PIN không đúng');
                if (next >= LOCK_MAX_FAIL_BEFORE_DELAY) {
                    setDelayUntil(Date.now() + LOCK_BRUTEFORCE_DELAY_SECONDS * 1000);
                }
                return;
            }
            setFailCount(0);
            setErrorText('');
            setLocked(false);
        });
    };

    const handleLogout = () => {
        if (window.confirm('Bạn chắc chắn muốn đăng xuất? Mọi thay đổi chưa lưu sẽ mất.')) {
            setSessionLocked(false);
            AntAuth.logOut();
        }
    };

    if (!locked) return null;

    const d = new Date(now);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const subtitle = `${hh}:${mm} · ${dd}/${mo}`;

    const remaining = Math.max(0, Math.ceil((delayUntil - now) / 1000));
    const errorDisplay = remaining > 0 ? `Thử lại sau ${remaining}s` : errorText;

    const userName = AntAuth.getSessionInfo('employee_name') || AntAuth.getSessionInfo('user_name') || '';

    const topSlot = (
        <div className="apt-lock-user">
            <div className="apt-lock-avatar">{(userName[0] || 'U').toUpperCase()}</div>
            <div className="apt-lock-name">{userName}</div>
        </div>
    );

    // "Đổi ca" CHỈ hiện khi: user bật manages_work_shift = 1 + business bật use_Module_AR + use_Module_POS
    // (đúng theo isShiftFeatureEnabled). Khi không bật, không có khái niệm ca → không hiện nút.
    const bottomActions = (
        <Fragment>
            <button type="button" className="apt-passcode-secondary-btn" onClick={() => setShowChangePin(true)}>
                Đổi mã PIN
            </button>
            {isShiftFeatureEnabled() ? (
                <button type="button" className="apt-passcode-secondary-btn" onClick={() => setShowSwitchShift(true)}>
                    Đổi ca
                </button>
            ) : null}
            <button type="button" className="apt-passcode-secondary-btn" onClick={handleLogout}>
                Đăng xuất
            </button>
        </Fragment>
    );

    // Lock screen mở PIN nhân viên (nếu đang on-shift) hoặc PIN tài khoản → chỉ hiện ghi chú
    // mã mặc định khi PIN tương ứng đang là default '111111'. Đổi PIN xong, BE refresh session
    // → flag flip → ghi chú tự ẩn.
    const onShift = !!AntAuth.getSessionInfo('employee_code');
    const showDefaultPinHint = onShift
        ? Number(AntAuth.getSessionInfo('has_default_employee_pin')) === 1
        : Number(AntAuth.getSessionInfo('is_default_user_pin')) === 1;
    const helperText = (showDefaultPinHint || changedNotice) ? (
        <span>
            {showDefaultPinHint ? <>Mã mặc định: <strong>111111</strong>. Đổi mã ở nút bên dưới.</> : null}
            {showDefaultPinHint && changedNotice ? <br /> : null}
            {changedNotice ? changedNotice : null}
        </span>
    ) : null;

    if (showChangePin) {
        const onChanged = (data) => {
            setShowChangePin(false);
            const tgt = data && data.target;
            const who = tgt === 'employee' ? (data.employee_name || data.employee_code) : (data.user_name || data.user_code);
            setChangedNotice(`Đã đổi PIN ${tgt === 'employee' ? 'nhân viên' : 'tài khoản'}: ${who || ''}`);
            // Optimistic: cập nhật flag ngay trong localStorage để ghi chú "Mã mặc định 111111" tự
            // ẩn ngay, không phải đợi refreshToken xong (refreshToken là async fire-and-forget).
            try {
                const k = lsKey('session-info');
                const info = JSON.parse(localStorage.getItem(k) || '{}');
                if (tgt === 'employee') {
                    info.has_default_employee_pin = 0;
                    if (!info.employee_code) info.is_default_user_pin = 0;
                } else {
                    info.is_default_user_pin = 0;
                    if (!info.employee_code) info.has_default_employee_pin = 0;
                }
                localStorage.setItem(k, JSON.stringify(info));
            } catch (e) { /* ignore */ }
            // Refresh session để BE xác nhận flag mới + bump state để re-render khi xong.
            try {
                AntAuth.refreshToken(false, null, () => setSessionVer((v) => v + 1));
            } catch (e) { /* ignore */ }
            setSessionVer((v) => v + 1);
        };
        return <AntChangePasscodePopup onClose={() => setShowChangePin(false)} onChanged={onChanged} />;
    }

    if (showSwitchShift) {
        // Khi user2 đổi ca thành công → BE đã refresh token, FE cần reload session.
        const onSwitchDone = () => {
            setShowSwitchShift(false);
            setLocked(false);
            try {
                window.location.reload();
            } catch (e) {
                // ignore
            }
        };
        // Pass session với employee_code hiện tại để overlay biết đây là Đổi ca.
        const switchSession = {
            employee_code: AntAuth.getSessionInfo('employee_code'),
            employee_name: AntAuth.getSessionInfo('employee_name'),
            store_name: AntAuth.getSessionInfo('store_name'),
            store_code: AntAuth.getSessionInfo('store_code')
        };
        return (
            <AntShiftActionOverlay
                mode="checkin"
                session={switchSession}
                onDone={onSwitchDone}
                onClose={() => setShowSwitchShift(false)}
            />
        );
    }

    return (
        <AntPasscodeOverlay
            title="Đã khoá"
            subtitle={subtitle}
            topSlot={topSlot}
            bottomActions={bottomActions}
            processing={processing}
            errorText={errorDisplay}
            errorKey={failCount}
            helperText={helperText}
            onComplete={handleComplete}
            dismissible={false}
        />
    );
}
